/**
 * TanStack Query hooks for `creative-preview`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	regenerate,
	remove,
	creativePreviewQueryKeys,
	type CreateCreativePreviewBody,
	type CreateCreativePreviewResponse,
	type ListCreativePreviewResponse,
	type RevokeCreativePreviewResponse,
} from "./api";

export function useCreativePreviewList(): UseQueryResult<
	ListCreativePreviewResponse,
	Error
> {
	return useQuery({
		queryKey: creativePreviewQueryKeys.list(),
		queryFn: list,
	});
}

export function useCreativePreviewCreate(): UseMutationResult<
	CreateCreativePreviewResponse,
	Error,
	CreateCreativePreviewBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: creativePreviewQueryKeys.all });
		},
	});
}

export function useCreativePreviewDelete(): UseMutationResult<
	RevokeCreativePreviewResponse,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: creativePreviewQueryKeys.all });
		},
	});
}

export function useCreativePreviewRegenerate(): UseMutationResult<
	CreateCreativePreviewResponse,
	Error,
	{ token: string; creativeId: string; expiresInMinutes?: number }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: regenerate,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: creativePreviewQueryKeys.all });
		},
	});
}
